import React, { useState, useEffect } from "react";
import { Grid, TextField, Autocomplete, Box } from "@mui/material";
import { Button } from "reactstrap";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "../CommonComponents/RightDrawer";
import {
  GSTfieldsConfig,
  GSTrequiredFields,
  GSTstages,
} from "../../utils/fieldsConfig";
import { formatDate, getIdNameIfAdmin } from "../../utils/commonutils";
import { errorSummary, scrollToFirstError } from "../../utils/formErrors";
import ModuleFollowups from "../CommonComponents/ModuleFollowups";
import AttachmentPanel from "../CommonComponents/AttachmentPanel";
import { MasterAutocomplete } from "../CommonComponents/MasterSelect";

const GSTLitigationModal = ({
  isOpen,
  mode,
  onClose,
  caseData,
  customerData,
  onSubmit,
  props,
  employeedata,
}) => {
  const history = useHistory();
  const employeeObj = getIdNameIfAdmin(props?.authData);
  const initialData = {
    caseId: caseData?.caseId || "",
    taxPayerName: caseData?.customerId || "",
    GSTIN: caseData?.GSTIN || "",
    subject: caseData?.subject || "",
    amount: caseData?.amount || "",
    stage: caseData?.stage || "",
    status: caseData?.status || "",
    assignedTo: caseData?.assignedTo || "",
    DRC01AReceived: formatDate(caseData?.DRC01AReceived) || "",
    replayToDRC01A: caseData?.replayToDRC01A || "",
    DRC01CauseDate: formatDate(caseData?.DRC01CauseDate) || "",
    replayToDRC01: formatDate(caseData?.replayToDRC01) || "",
    additionalClarification: caseData?.additionalClarification || "",
    hearingNotice: formatDate(caseData?.hearingNotice) || "",
    wrritenSubmissionFiled: caseData?.wrritenSubmissionFiled || "",
    hearing1: caseData?.hearing1 || "",
    hearing2: caseData?.hearing2 || "",
    hearing3: caseData?.hearing3 || "",

    orderDate: formatDate(caseData?.orderDate) || "",
    orderPassed: formatDate(caseData?.orderPassed) || "",
    demandStatus: caseData?.demandStatus || "",
    DRC07issued: formatDate(caseData?.DRC07issued) || "",

    appealFiledDate: formatDate(caseData?.appealFiledDate) || "",
    appealAckNo: caseData?.appealAckNo || "",
    appealHearing1: formatDate(caseData?.appealHearing1) || "",
    appealHearing2: formatDate(caseData?.appealHearing2) || "",
    appellateOrderPassed: formatDate(caseData?.appellateOrderPassed) || "",

    highCourtWritFiled: formatDate(caseData?.highCourtWritFiled) || "",
    HCHearing1: formatDate(caseData?.HCHearing1) || "",
    HCHearing2: formatDate(caseData?.HCHearing2) || "",
    HCHearing3: formatDate(caseData?.HCHearing3) || "",
    HCHearing4: formatDate(caseData?.HCHearing4) || "",
    highCourtOrder: formatDate(caseData?.highCourtOrder) || "",
  };
  const [formData, setFormData] = useState(initialData);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    if (caseData) setFormData(initialData);
    setErrors({});
    setSubmitError("");
  }, [caseData, isOpen, mode]);

  const getVisibleFields = () => {
    if (mode === "add") return GSTstages[0];

    if (mode === "edit") {
      let visible = [];

      for (let i = 0; i < GSTstages.length; i++) {
        const currentStage = GSTstages[i];
        visible = [...visible, ...currentStage];
        if (
          (currentStage.includes("hearing1") &&
            formData.hearing1 === "Completed") ||
          (currentStage.includes("hearing2") &&
            formData.hearing2 === "Completed") ||
          (currentStage.includes("hearing3") &&
            formData.hearing3 === "Completed")
        ) {
          const orderIndex = GSTstages.findIndex((s) =>
            s.includes("orderDate"),
          );
          for (let j = orderIndex; j < GSTstages.length; j++) {
            visible = [...visible, ...GSTstages[j]];
            const stageFilled = GSTstages[j].some((field) => formData[field]);
            if (
              GSTstages[j].includes("demandStatus") &&
              formData.demandStatus === "Dropped"
            ) {
              return visible;
            }
            if (!stageFilled) break;
          }
          return visible;
        }

        const stageFilled = currentStage.some((field) => formData[field]);
        if (!stageFilled) break;
      }

      return visible;
    }

    return [];
  };

  const visibleFields = getVisibleFields();

  const validateForm = () => {
    const newErrors = {};
    GSTrequiredFields.forEach((field) => {
      if (!formData[field]) {
        newErrors[field] = `${GSTfieldsConfig[field]?.label || field} is required`;
      }
    });
    setErrors(newErrors);
    return newErrors;
  };

  const handleSubmit = async () => {
    const newErrors = validateForm();
    const failed = GSTrequiredFields.filter((field) => newErrors[field]);
    if (failed.length) {
      setSubmitError("");
      scrollToFirstError(failed);
      return;
    }
    const result = await onSubmit(formData);
    if (result?.ok === false) {
      const fieldErrors = result.fieldErrors || {};
      setErrors(fieldErrors);
      setSubmitError(result.message || "Please fix the highlighted fields.");
      scrollToFirstError(Object.keys(fieldErrors));
    }
  };

  const clearError = (name) =>
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const { [name]: _cleared, ...rest } = prev;
      return rest;
    });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    clearError(e.target.name);
  };

  return (
    <Modal isOpen={isOpen} size="xl" toggle={onClose}>
      <ModalHeader toggle={onClose} kicker="GST litigation">
        {mode === "add"
          ? "New GST case"
          : "Edit GST case"}
      </ModalHeader>
      <ModalBody>
        {caseData?.inquiryId ? (
          <p className="small-text" style={{ marginBottom: 12 }}>
            Linked from an inquiry ·{" "}
            <button
              type="button"
              className="text-[13px] font-semibold text-[#1a3366] hover:underline bg-transparent border-0 p-0"
              onClick={() =>
                history.push(
                  `/inquiriedetail/${
                    caseData.inquiryId?._id || caseData.inquiryId
                  }`
                )
              }
            >
              Open inquiry
            </button>
          </p>
        ) : null}
        {submitError || Object.keys(errors).length ? (
          <p className="form-error-summary">{submitError || errorSummary(Object.keys(errors).length)}</p>
        ) : null}
        <Grid container spacing={2}>
          {visibleFields?.map((name) => {
            const field = GSTfieldsConfig[name];
            if (!field) return null;
            return (
              <Grid item xs={12} sm={6} key={name} data-field={name}>
                <p className="small-text">{field.label}</p>
                {field.type === "select" ? (
                  <div className={errors[name] ? "field-wrap-error" : undefined}>
                  {name === "assignedTo" ? (
                    <Autocomplete
                      options={employeedata || []}
                      value={formData[name] || null}
                      getOptionLabel={(option) => option?.name || ""}
                      onChange={(e, val) => {
                        clearError(name);
                        setFormData({
                          ...formData,
                          [name]: val,
                        });
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      slotProps={{ popper: { sx: { zIndex: 20000 } } }}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          {option?.name}
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select employee" />
                      )}
                    />
                  ) : name === "taxPayerName" ? (
                    <Autocomplete
                      options={customerData || []}
                      value={formData[name] || null }
                      getOptionLabel={(option) => option?.companyName || ""}
                      onChange={(e, val) => {
                        clearError(name);
                        setFormData({
                          ...formData,
                          [name]: val
                        });
                      }}
                      isOptionEqualToValue={(option, value) =>
                        option?._id === value?._id
                      }
                      slotProps={{ popper: { sx: { zIndex: 20000 } } }}
                      renderOption={(props, option) => (
                        <Box component="li" {...props}>
                          {option?.companyName}
                        </Box>
                      )}
                      renderInput={(params) => (
                        <TextField {...params} placeholder="Select customer" />
                      )}
                    />
                  ) : field.master ? (
                    <MasterAutocomplete
                      group={field.master}
                      fallback={(field.options || []).map((opt) => ({ value: opt, label: opt }))}
                      value={formData[name] || ""}
                      onChange={(e, newValue) => {
                        clearError(name);
                        setFormData({ ...formData, [name]: newValue });
                      }}
                      placeholder="Select"
                    />
                  ) : (
                    // NORMAL SELECT
                    <Autocomplete
                      options={field?.options || []}
                      value={formData[name] || ""}
                      getOptionLabel={(option) => option || ""}
                      onChange={(e, newValue) => {
                        clearError(name);
                        setFormData({ ...formData, [name]: newValue });
                      }}
                      slotProps={{ popper: { sx: { zIndex: 20000 } } }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select"
                          size="large"
                        />
                      )}
                    />
                  )}
                  </div>
                ) : (
                  <input
                    className={`input-main${errors[name] ? " input-main-error" : ""}`}
                    type={field.type}
                    name={name}
                    value={formData[name] || ""}
                    onChange={handleChange}
                    placeholder={field.label}
                  />
                )}
                {errors[name] && (
                  <p className="error-message">{errors[name]}</p>
                )}
              </Grid>
            );
          })}
        </Grid>
        {caseData?._id && (
          <Box mt={3}>
            <ModuleFollowups
              moduleName="case_management"
              recordId={caseData._id}
              customerId={caseData?.customerId?._id || caseData?.customerId}
              props={props}
            />
            <AttachmentPanel moduleName="case_management" recordId={caseData._id} />
          </Box>
        )}
      </ModalBody>
      <ModalFooter>
        <Button className="btn btn-border" onClick={onClose}>
          Cancel
        </Button>
        <Button className="btn btn-colored" onClick={handleSubmit}>
          {caseData?._id ? "Save changes" : "Create case"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default GSTLitigationModal;
